import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, CalendarClock, CalendarDays, CheckCircle2, ClipboardCheck, Dumbbell, FileSignature, Gift, ListTodo,
  MessageSquare, NotebookPen, Search, Stethoscope,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge, tagDe } from '@/components/CoachKundeninfo';

/* Coach-Heute (Coach-Cockpit W2.1, 29.10.2026): neue Startseite von /coach. Kennzahlen, Check-in-Quote (Mo–So), EINE Liste
   „Heute erledigen“ aus allen Quellen (Reihenfolge rechnet der Server, `coach/heute.py` RANG), Kundenkarten mit Filter und
   der private Notizblock (Auto-Save). Jede Zeile hat eine Direktaktion: `gehe` (Ziel auf /coach) oder `aufruf` (POST unter
   /api/coach). Trainer sehen ihre Kunden, die Leitung alle — mit Trainerfilter. */

export const ART_ICON = {
  nachrichten: MessageSquare, checkup_abschliessen: Stethoscope, kritisch: AlertTriangle, checkin_auswerten: ClipboardCheck,
  checkup_vorbereiten: Stethoscope, pt_erbracht: CheckCircle2, angebot_nachfassen: FileSignature, kraftlog: Dumbbell,
  paket_endet: CalendarClock, gratis_endet: Gift, aufgabe: ListTodo,
};
const BEREICH_TEXT = { training: 'Training', ernaehrung: 'Ernährung', pt: 'PT', phase: 'Phase' };
const FILTER = [['alle', 'Alle'], ['handlungsbedarf', 'Handlungsbedarf'], ['aktuell', 'Aktuell'], ['urlaub', 'Im Urlaub']];
const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);

export function useAktion(coach, gehe, nachher) {
  const [busy, setBusy] = useState('');
  const ausfuehren = useCallback(async (aktion, schluessel) => {
    if (!aktion) return;
    if (aktion.art === 'gehe') { gehe(aktion.ziel); return; }
    setBusy(schluessel || aktion.pfad);
    try {
      await coach.request({ method: aktion.methode || 'post', url: aktion.pfad, data: aktion.body || {} });
      toast.success(aktion.label === 'Erledigt' ? 'Erledigt' : 'Gespeichert');
      nachher?.();
    } catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
    setBusy('');
  }, [coach, gehe, nachher]);
  return [ausfuehren, busy];
}

const knopf = 'inline-flex min-h-[44px] items-center justify-center rounded-[10px] px-3.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
export const knopfHaupt = `${knopf} bg-marke text-white hover:opacity-90`;
export const knopfZweit = `${knopf} border border-border bg-card hover:bg-muted`;
export const chip = (an) => `min-h-[40px] rounded-full border px-3.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'border-transparent bg-foreground text-background' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`;

const mKnopf = 'inline-flex min-h-[44px] items-center justify-center rounded-[10px] border border-border bg-muted px-4 text-sm font-bold transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
const mKnopfHaupt = 'inline-flex min-h-[44px] items-center justify-center rounded-[10px] border border-primary bg-primary px-4 text-sm font-bold text-white transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
const mChip = (an) => `inline-flex min-h-[44px] items-center rounded-full border px-[11px] py-[5px] text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground/80 hover:text-foreground'}`;
const KARTE = 'rounded-[14px] border border-border bg-card px-5 py-[18px]';

const KAL_ART = { pt: 'PT', gruppe: 'Gruppe', checkup: 'Check-up', blocker: 'Belegt', kurs: 'Kurs' };

/* „Heute im Kalender“ (Integration W3, 29.10.2026): die heutigen Termine des Trainers aus derselben Quelle wie der
   Coach-Kalender (`kalender_heute` = `coach.kalender.heute_kompakt`). Kurse ausgegraut, Abgesagtes durchgestrichen.
   Antippen öffnet den Kalender bzw. die Akte des Kunden. */
function KalenderHeute({ k, gehe }) {
  if (!k) return null;
  const liste = k.eintraege || [];
  return (
    <section className={KARTE} data-testid="coach-heute-kalender">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-base font-bold"><CalendarDays className="h-4 w-4 text-marke" aria-hidden="true" /> Heute im Kalender</h3>
        <button type="button" className="min-h-[44px] px-1 text-sm font-semibold text-muted-foreground hover:text-foreground" onClick={() => gehe({ ansicht: 'kalender' })}>Kalender</button>
      </div>
      {k.hinweis && <p className="mt-2 text-xs text-muted-foreground">{k.hinweis}</p>}
      {!k.hinweis && !liste.length && <p className="mt-2 text-sm text-muted-foreground">Heute keine Termine.</p>}
      {liste.length > 0 && (
        <ul className="mt-2 divide-y divide-border/60">
          {liste.map((e) => {
            const kunde = e.art === 'gruppe' ? `${e.titel || 'Gruppe'}${e.anzahl ? ` · ${e.anzahl}` : ''}` : e.art === 'kurs' ? e.titel : (e.kunde || e.titel);
            const ziel = e.lead_id && ['pt', 'checkup'].includes(e.art) ? { ansicht: 'kunde', kunde: e.lead_id } : { ansicht: 'kalender' };
            return (
              <li key={e.id}>
                <button type="button" onClick={() => gehe(ziel)}
                  className={`flex min-h-[44px] w-full items-center gap-3 py-1.5 text-left text-sm ${e.nur_ansicht ? 'text-muted-foreground' : ''} ${e.abgesagt ? 'line-through opacity-60' : ''}`}>
                  <span className="w-12 shrink-0 font-bold tabular-nums">{e.start || '–'}</span>
                  <span className="min-w-0 flex-1 truncate"><span className="font-semibold">{KAL_ART[e.art] || e.art}</span>{e.art !== 'blocker' && kunde ? ` · ${kunde}` : ''}</span>
                  {e.erbracht && <CheckCircle2 className="h-4 w-4 shrink-0 text-marke" aria-label="erbracht" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Kachel({ label, wert, sub, subMarke, testId }) {
  return (
    <div className={KARTE} data-testid={testId}>
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="coach-zahl text-3xl font-extrabold leading-[1.1] tabular-nums">{wert ?? '–'}</div>
      {sub && <div className={`text-[13px] ${subMarke ? 'text-marke' : 'text-muted-foreground'}`}>{sub}</div>}
    </div>
  );
}

function Ring({ quote }) {
  const r = 42;
  const u = 2 * Math.PI * r;
  const anteil = quote == null ? 0 : Math.max(0, Math.min(100, quote)) / 100;
  return (
    <svg viewBox="0 0 100 100" className="h-[74px] w-[74px] shrink-0" role="img" aria-label={quote == null ? 'Keine fälligen Check-ins' : `${quote} Prozent abgeschlossen`}>
      <circle cx="50" cy="50" r={r} fill="none" strokeWidth="10" className="stroke-muted" />
      <circle cx="50" cy="50" r={r} fill="none" strokeWidth="10" strokeLinecap="round" className="stroke-marke"
        strokeDasharray={`${u * anteil} ${u}`} transform="rotate(-90 50 50)" />
      <text x="50" y="55" textAnchor="middle" className="coach-zahl fill-foreground text-[22px] font-extrabold">{quote == null ? '–' : `${quote}%`}</text>
    </svg>
  );
}

export function TrainerFilter({ liste, wert, onWahl }) {
  if (!liste?.length) return null;
  return (
    <label className="flex items-center gap-2 text-sm font-semibold">
      <span className="text-muted-foreground">Trainer</span>
      <select value={wert || ''} onChange={(e) => onWahl(e.target.value || null)} data-testid="coach-trainerfilter"
        className="min-h-[44px] rounded-[10px] border border-border bg-card px-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <option value="">Alle Kunden</option>
        {liste.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
    </label>
  );
}

function Notizblock({ coach }) {
  const [text, setText] = useState(null);
  const [stand, setStand] = useState('');
  const timer = useRef(null);
  const letzter = useRef('');
  useEffect(() => {
    coach.get('/notiz').then((r) => { setText(r.data.text || ''); letzter.current = r.data.text || ''; }).catch(() => setText(''));
    return () => clearTimeout(timer.current);
  }, [coach]);
  const speichern = useCallback((wert) => {
    if (wert === letzter.current) return;
    setStand('speichert …');
    coach.put('/notiz', { text: wert }).then(() => { letzter.current = wert; setStand('gespeichert'); })
      .catch((e) => setStand(fehlerText(e, 'nicht gespeichert')));
  }, [coach]);
  const aendern = (wert) => {
    setText(wert);
    setStand('');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => speichern(wert), 800);
  };
  return (
    <section className={KARTE} data-testid="coach-notizblock">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-base font-bold"><NotebookPen className="h-4 w-4 text-marke" aria-hidden="true" /> Mein Notizblock</h3>
        <span className="text-[13px] font-semibold text-muted-foreground">privat</span>
      </div>
      <p className="mb-1 text-[13px] text-muted-foreground">Nur für dich sichtbar</p>
      {text === null ? <Skeleton className="h-32" /> : (
        <textarea value={text} onChange={(e) => aendern(e.target.value)} onBlur={() => { clearTimeout(timer.current); speichern(text); }}
          maxLength={5000} rows={6} aria-label="Notizblock" data-testid="coach-notiz"
          className="w-full resize-y rounded-[10px] border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
      )}
      <p className="mt-1.5 min-h-[1.25rem] text-[13px] text-muted-foreground" aria-live="polite">{stand}</p>
    </section>
  );
}

export function Zeile({ z, onAktion, busy }) {
  const schluessel = `${z.art}:${z.lead_id}:${z.aufgabe_id || z.termin_id || ''}`;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 border-t border-border py-3 first:border-t-0" data-testid={`heute-zeile-${z.art}`}>
      <div className="min-w-0 flex-1 basis-56">
        <p className="break-words font-bold">{z.name || 'Kunde'} <span className="font-semibold text-muted-foreground">· {z.titel}</span></p>
        <p className="break-words text-[13px] text-muted-foreground">
          {z.ueberfaellig && <span className="mr-1.5 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-extrabold text-red-700 dark:bg-[#3a1c18] dark:text-[#ff9a8a]">überfällig</span>}
          {z.text}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        {z.zweit && <button type="button" className={mKnopf} disabled={busy === schluessel} onClick={() => onAktion(z.zweit, schluessel)}>{z.zweit.label}</button>}
        <button type="button" className={mKnopf} disabled={busy === schluessel} onClick={() => onAktion(z.aktion, schluessel)} data-testid="heute-aktion">{z.aktion.label}</button>
      </div>
    </li>
  );
}

function Kundenkarte({ k, onAktion }) {
  const bereiche = k.bereiche.map((b) => BEREICH_TEXT[b]).join(' · ');
  const grund = [k.letztes_ereignis ? `${k.letztes_ereignis.text} · ${tagDe(k.letztes_ereignis.tag)}` : null, bereiche || null, k.urlaub ? 'im Urlaub' : null].filter(Boolean).join(' · ');
  const delta = k.gewicht?.delta != null ? `${k.gewicht.delta > 0 ? '+' : '−'}${String(Math.abs(k.gewicht.delta)).replace('.', ',')} kg` : null;
  const werte = [delta, k.woche?.text, k.offene_punkte ? `${k.offene_punkte} offene Punkte` : null].filter(Boolean).join(' · ');
  return (
    <article className="flex flex-col gap-2 rounded-xl border border-border p-3.5" data-testid="coach-kundenkarte">
      <div className="flex items-center justify-between gap-2">
        <h4 className="min-w-0 break-words font-bold">{k.name}</h4>
        <StatusBadge status={k.status} />
      </div>
      <p className="text-[13px] text-muted-foreground">{grund || '–'}</p>
      <p className="text-[13px]">{werte || '–'}</p>
      <button type="button" className={`self-start ${k.offene_punkte ? mKnopfHaupt : mKnopf}`} onClick={() => onAktion(k.aktion, `karte:${k.lead_id}`)}>{k.aktion.label}</button>
    </article>
  );
}

export default function CoachHeute({ coach, gehe, setUntertitel }) {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [trainer, setTrainer] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [bereich, setBereich] = useState('');
  const [suche, setSuche] = useState('');
  const lade = useCallback(() => {
    coach.get('/heute', { params: trainer ? { trainer } : {} }).then((r) => { setD(r.data); setFehler(''); })
      .catch((e) => setFehler(fehlerText(e, 'Heute nicht geladen')));
  }, [coach, trainer]);
  useEffect(() => { lade(); }, [lade]);
  const [ausfuehren, busy] = useAktion(coach, gehe, lade);
  const anzahl = d?.anzahl;
  useEffect(() => {
    if (!setUntertitel || anzahl == null) return;
    setUntertitel(anzahl ? `${anzahl} ${anzahl === 1 ? 'Aufgabe braucht' : 'Aufgaben brauchen'} heute deine Aufmerksamkeit.` : 'Heute ist alles erledigt.');
  }, [anzahl, setUntertitel]);

  const karten = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return (d?.karten || []).filter((k) => (filter === 'alle' || (filter === 'handlungsbedarf' && k.handlungsbedarf)
      || (filter === 'aktuell' && !k.handlungsbedarf && !k.urlaub) || (filter === 'urlaub' && k.urlaub))
      && (!bereich || k.bereiche.includes(bereich)) && (!q || k.name.toLowerCase().includes(q)));
  }, [d, filter, bereich, suche]);

  if (fehler) return <p className="text-sm text-destructive" role="alert">{fehler}</p>;
  if (!d) return <div className="space-y-3" aria-busy="true"><Skeleton className="h-24 rounded-[14px]" /><Skeleton className="h-64 rounded-[14px]" /></div>;
  const k = d.kpi;
  const q = d.checkin_quote;
  const filterAn = (id) => filter === id;
  return (
    <div className="flex flex-col gap-[18px]" data-testid="coach-heute">
      {d.trainer_liste?.length > 0 && <div className="flex justify-end"><TrainerFilter liste={d.trainer_liste} wert={trainer} onWahl={setTrainer} /></div>}

      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-[1.7fr_repeat(5,minmax(0,1fr))]">
        <div className={`${KARTE} col-span-2 flex items-center gap-3.5 md:col-span-1`} data-testid="coach-checkin-quote">
          <Ring quote={q.quote} />
          <div className="min-w-0">
            <div className="font-bold">Check-in-Quote</div>
            <div className="text-[13px] text-muted-foreground">
              {q.quote == null ? 'Diese Woche noch nichts fällig' : `${q.abgeschlossen} von ${q.faellig} fälligen`} · stärkster Tag {q.staerkster_tag || '–'}
            </div>
            <div className="text-xs text-muted-foreground">Mo {tagDe(q.von)} – So {tagDe(q.bis)}</div>
          </div>
        </div>
        <Kachel label="Aktive Kunden" wert={k.aktive_kunden} testId="kpi-aktiv"
          sub={`Training ${k.aktive_je_bereich.training} · Ernährung ${k.aktive_je_bereich.ernaehrung} · PT ${k.aktive_je_bereich.pt} · Phase ${k.aktive_je_bereich.phase}`} />
        <Kachel label="Offene Check-ins" wert={k.offene_checkins} sub="warten auf dich" subMarke testId="kpi-checkins" />
        <Kachel label="Ohne Kommentar" wert={k.kraftlogs_ohne_kommentar} sub="Kraftlogs der letzten 14 Tage" testId="kpi-kraftlog" />
        <Kachel label="Ungelesen" wert={k.ungelesen} sub="Nachrichten" subMarke testId="kpi-nachrichten" />
        <Kachel label="Offene Angebote" wert={k.offene_angebote} testId="kpi-angebote" />
      </section>

      <div className="flex flex-wrap items-start gap-[18px]">
        <section className={`${KARTE} min-w-0 flex-[2_1_460px]`} aria-labelledby="heute-liste-titel">
          <h3 id="heute-liste-titel" className="mb-3 text-base font-bold" data-testid="coach-heute-anzahl">Heute erledigen</h3>
          {!d.liste.length ? <p className="py-8 text-center text-sm text-muted-foreground">Nichts offen. Stark!</p> : (
            <ul data-testid="coach-heute-liste">
              {d.liste.map((z) => <Zeile key={`${z.art}:${z.lead_id}:${z.aufgabe_id || z.termin_id || z.angebot_id || ''}`} z={z} onAktion={ausfuehren} busy={busy} />)}
            </ul>
          )}
        </section>
        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-[18px]">
          <Notizblock coach={coach} />
          <KalenderHeute k={d.kalender_heute} gehe={gehe} />
        </div>
      </div>

      <section className={KARTE} aria-labelledby="heute-kunden-titel">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2.5">
          <h3 id="heute-kunden-titel" className="text-base font-bold">Deine Kunden</h3>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter">
            {FILTER.map(([id, label]) => (
              <button key={id} type="button" className={mChip(filterAn(id))} aria-pressed={filterAn(id)} onClick={() => setFilter(id)} data-testid={`coach-filter-${id}`}>
                {label} {d.filter[id]}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-1.5" role="group" aria-label="Bereich">
          <button type="button" className={mChip(!bereich)} aria-pressed={!bereich} onClick={() => setBereich('')}>Alle Bereiche</button>
          {Object.entries(BEREICH_TEXT).map(([id, label]) => (
            <button key={id} type="button" className={mChip(bereich === id)} aria-pressed={bereich === id} onClick={() => setBereich(id)} data-testid={`coach-bereich-${id}`}>{label}</button>
          ))}
          <label className="relative ml-auto block w-56 max-w-full">
            <span className="sr-only">Kunden suchen</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Kunde suchen" data-testid="coach-heute-suche"
              className="h-11 w-full rounded-[10px] border border-border bg-muted pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          </label>
        </div>
        {!karten.length ? <p className="rounded-[14px] border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Keine Kunden in dieser Auswahl.</p> : (
          <div className="mt-2.5 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
            {karten.map((kk) => <Kundenkarte key={kk.lead_id} k={kk} onAktion={ausfuehren} />)}
          </div>
        )}
      </section>
    </div>
  );
}
